import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { documentService } from '@/services'
import { useAuth } from '@/hooks/useAuth'
import { type DocumentFormData } from '@/lib/validations/document'
import { toast } from 'sonner'
import type { Document, Project, Task } from '@/types'
import { useWorkspaceData } from '@/hooks/useWorkspaceData'
import { useDocumentAssociations } from '@/hooks/useDocumentAssociations'
import { useWorkspaceChange } from '@/hooks/useWorkspaceChange'
import { WorkspaceChangeConfirmDialog } from '@/components/workspaces/WorkspaceChangeConfirmDialog'
import { PageContainer, PageHeader, LoadingState } from '@/components/layout'
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges'
import { UnsavedChangesDialog } from '@/components/ui/unsaved-changes-dialog'
import { DocumentForm } from '@/components/documents/DocumentForm'

export default function DocumentEditPage() {
  const { documentId } = useParams<{ documentId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [loading, setLoading] = useState(false)
  const [loadingDocument, setLoadingDocument] = useState(true)
  const [document, setDocument] = useState<Document | null>(null)
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string>('')
  const [isDirty, setIsDirty] = useState(false)

  // Track unsaved changes
  const unsavedChanges = useUnsavedChanges({
    isDirty,
  })

  // Load workspace data
  const { workspaces, projects, tasks, users } = useWorkspaceData({
    userId: user?.id,
    workspaceId: selectedWorkspaceId,
  })

  // Manage associations
  const {
    selectedProjects,
    selectedTasks,
    selectedUsers,
    handleAddProject,
    handleRemoveProject,
    handleAddTask,
    handleRemoveTask,
    handleAddUser,
    handleRemoveUser,
    setAssociations,
    filterAssociationsByAvailable,
    getCounts,
  } = useDocumentAssociations()

  // Handle workspace change confirmation
  const workspaceChange = useWorkspaceChange({
    onConfirm: (newWorkspaceId) => {
      setSelectedWorkspaceId(newWorkspaceId)
    },
    currentWorkspaceId: document?.workspace.id,
    checkChange: true,
  })

  // Clear associations that don't belong to the new workspace when workspace changes
  useEffect(() => {
    if (selectedWorkspaceId && document) {
      // Only filter if this is a workspace change (not initial load)
      if (selectedWorkspaceId !== document.workspace.id) {
        filterAssociationsByAvailable(projects, tasks, users)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWorkspaceId, projects, tasks, users])

  const handleWorkspaceChange = (value: string) => {
    workspaceChange.handleWorkspaceChange(value, getCounts())
  }

  useEffect(() => {
    if (!documentId) return

    const loadDocument = async () => {
      try {
        setLoadingDocument(true)
        const documentData = await documentService.getById(documentId)
        if (documentData) {
          setDocument(documentData)
          setSelectedWorkspaceId(documentData.workspace.id)

          // Set existing associations
          setAssociations(
            documentData.projects as Project[],
            documentData.tasks as Task[],
            documentData.users.filter((u) => u.id !== documentData.creator.id)
          )
        } else {
          toast.error('Document not found')
          navigate('/documents')
        }
      } catch (error) {
        console.error('Failed to load document:', error)
        toast.error('Failed to load document')
        navigate('/documents')
      } finally {
        setLoadingDocument(false)
      }
    }

    loadDocument()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId])

  const onSubmit = async (data: DocumentFormData) => {
    if (!documentId || !user || !document) return

    setLoading(true)
    try {
      // Update basic document data
      await documentService.update(documentId, data)

      // Update associations by reconstructing the document with new associations
      const updatedDoc = await documentService.getById(documentId)
      if (updatedDoc) {
        // Update projects
        const projectRefs = selectedProjects.map((p) => ({
          id: p.id,
          name: p.name,
          description: p.description,
        }))

        // Update tasks
        const taskRefs = selectedTasks.map((t) => ({
          id: t.id,
          name: t.name,
          description: t.description,
        }))

        // Update users (always include creator)
        const userRefs = [document.creator, ...selectedUsers]

        await documentService.update(documentId, {
          projects: projectRefs,
          tasks: taskRefs,
          users: userRefs,
        } as Partial<Document>)
      }

      // Allow navigation before resetting dirty state and navigating
      unsavedChanges.allowNavigation()
      setIsDirty(false)

      toast.success('Document updated successfully')
      navigate(`/documents/${documentId}`)
    } catch (error) {
      console.error('Failed to update document:', error)
      toast.error('Failed to update document')
    } finally {
      setLoading(false)
    }
  }

  if (loadingDocument) {
    return (
      <PageContainer maxWidth="full">
        <LoadingState text="Loading document..." />
      </PageContainer>
    )
  }

  if (!document) {
    return null
  }

  return (
    <PageContainer maxWidth="full">
      <PageHeader title={document.name} titlePrefix="Edit Document:" />

      <DocumentForm
        mode="edit"
        defaultValues={{
          name: document.name,
          description: document.description,
          url: document.url,
          workspaceId: document.workspace.id,
        }}
        document={document}
        workspaces={workspaces}
        projects={projects}
        tasks={tasks}
        users={users}
        selectedProjects={selectedProjects}
        selectedTasks={selectedTasks}
        selectedUsers={selectedUsers}
        onAddProject={(id) => handleAddProject(id, projects)}
        onRemoveProject={handleRemoveProject}
        onAddTask={(id) => handleAddTask(id, tasks)}
        onRemoveTask={handleRemoveTask}
        onAddUser={(id) => handleAddUser(id, users)}
        onRemoveUser={handleRemoveUser}
        onWorkspaceChange={handleWorkspaceChange}
        onSubmit={onSubmit}
        onCancel={() => navigate(`/documents/${documentId}`)}
        submitText={loading ? 'Saving...' : 'Save Changes'}
        loading={loading}
        title="Document Details"
        description="Update the document information below"
        onDirtyChange={setIsDirty}
      />

      {/* Workspace Change Confirmation Dialog */}
      <WorkspaceChangeConfirmDialog
        open={workspaceChange.showDialog}
        onOpenChange={() => workspaceChange.cancelChange()}
        onConfirm={workspaceChange.confirmChange}
        onCancel={workspaceChange.cancelChange}
        projectCount={selectedProjects.length}
        taskCount={selectedTasks.length}
        userCount={selectedUsers.length}
        description="Changing the workspace will remove any associated projects, tasks, and users that don't belong to the new workspace. This action cannot be undone."
      />

      {/* Unsaved Changes Dialog */}
      <UnsavedChangesDialog
        open={unsavedChanges.showDialog}
        onOpenChange={(open) => !open && unsavedChanges.handleCancelNavigation()}
        onConfirm={unsavedChanges.handleConfirmNavigation}
        onCancel={unsavedChanges.handleCancelNavigation}
      />
    </PageContainer>
  )
}
